import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IReducer } from '../../../../../../redux/configureStore';
import Card from '../../../../../../shared/components/Card';
import {
  CardLabel,
  CardValue,
  LabelAndValueContainer,
} from '../../../../../../shared/components/Card/components/CardComponents';
import theme from '../../../../../../styles/theme';
import checkValue from '../../../../../../utils/values';
import BarChart from '../components/CardWithBarChart/components/BarChart';
import CardWithTable from '../components/CardWithTable';
import Frame from './components/Frame';
import PdfMonthlyPartnersPassedOn from './components/PdfMonthlyPartnersPassedOn';

function MonthlyDashboard() {
  const profilePartnerState = useSelector(
    (reducer: IReducer) => reducer.partnerProfile
  );
  const faresFilterState = useSelector(
    (reducer: IReducer) => reducer.faresFilter
  );
  const monthlyFaresState = useSelector(
    (reducer: IReducer) => reducer.monthlyFares
  );

  const globalIsActive = Object.prototype.hasOwnProperty.call(
    monthlyFaresState.income.value,
    'Fiducia'
  );

  const hasData = useMemo(() => {
    if (
      monthlyFaresState.partnerPassedOn.length > 0 &&
      profilePartnerState.nivel === 2
    ) {
      return true;
    }
    return false;
  }, [monthlyFaresState.partnerPassedOn, profilePartnerState.nivel]);

  let subtitle = '';

  switch (monthlyFaresState.accounts.type) {
    case 'pf':
      subtitle = 'Movimento - Pessoa Física';
      break;
    case 'pj':
      subtitle = 'Movimento - Pessoa Jurídica';
      break;
    case 'todos':
      subtitle = 'Todos os tipos de contas';
      break;
    case 'consignada':
      subtitle = 'Consignada';
      break;
    case 'escrow':
      subtitle = 'Escrow';
      break;
    case 'TIB':
      subtitle = 'TIB';
      break;

    default:
      break;
  }

  function isPartnerGlobalBarChart() {
    if (faresFilterState.searched.partner === 'global') {
      return true;
    }
    return false;
  }

  return (
    <Frame.Base
      isActive={monthlyFaresState.isActive}
      globalIsActive={globalIsActive}>
      <Card
        isActive
        title="Contas"
        subtitle={subtitle}
        display="flex"
        gridArea="accounts"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.lightGray}>
            {checkValue(monthlyFaresState.accounts.total)}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Solicitações de abertura</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.accounts.openedAccounts)}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Encerradas no período</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(monthlyFaresState.accounts.closedAccounts)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive
        title="Estado das Contas Abertas"
        subtitle={subtitle}
        display="flex"
        gridArea="detailedAccounts"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Abertas no período</CardLabel>
          <CardValue color={theme.colors.active}>
            {checkValue(monthlyFaresState.accounts.activeAccounts)}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Pendentes</CardLabel>
          <CardValue color={theme.colors.pending}>
            {checkValue(monthlyFaresState.accounts.pendingAccounts)}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Rejeitadas</CardLabel>
          <CardValue color={theme.colors.rejected}>
            {checkValue(monthlyFaresState.accounts.rejectedAccounts)}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Bloqueadas</CardLabel>
          <CardValue color={theme.colors.blocked}>
            {checkValue(monthlyFaresState.accounts.blockedAccounts)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Resultado"
        subtitle={subtitle}
        display="flex"
        gridArea="income"
        justifyContent="center"
        flexDirection="column"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue
            color={
              monthlyFaresState.income.value >= 0
                ? theme.colors.emphasis
                : theme.colors.red
            }>
            {checkValue(monthlyFaresState.income.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Tarifas Recebidas"
        subtitle={subtitle}
        display="flex"
        gridArea="income"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(
              monthlyFaresState.income.value.ambiente_382
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fidúcia</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.income.value.Fiducia).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.income.value.Parceiro).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Tarifas Recebidas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresReceived"
        justifyContent="center"
        flexDirection="column"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.faresReceived.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.faresReceived.quantity)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Tarifas Repassadas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresPassedOn"
        justifyContent="center"
        flexDirection="column"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(monthlyFaresState.faresPassedOn.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(monthlyFaresState.faresPassedOn.quantity)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Resultados"
        subtitle={subtitle}
        display="flex"
        gridArea="result"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(monthlyFaresState.result.ambiente_382).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fidúcia</CardLabel>
          <CardValue
            color={
              monthlyFaresState.result.Fiducia >= 0
                ? theme.colors.emphasis
                : theme.colors.red
            }>
            {checkValue(monthlyFaresState.result.Fiducia).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue
            color={
              monthlyFaresState.result.Parceiro >= 0
                ? theme.colors.emphasis
                : theme.colors.red
            }>
            {checkValue(monthlyFaresState.result.Parceiro).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive
        title="Tarifas Pendentes"
        subtitle={subtitle}
        display="flex"
        gridArea="faresNotReceived"
        justifyContent="center"
        flexDirection="column"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(monthlyFaresState.faresToReceive.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(monthlyFaresState.faresToReceive.quantity)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive
        title="Tarifas Provisionadas"
        subtitle={subtitle}
        display="flex"
        gridArea="faresProvider"
        justifyContent="center"
        flexDirection="column"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.lightGray}>
            {checkValue(monthlyFaresState.provider.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.lightGray}>
            {checkValue(monthlyFaresState.provider.quantity)}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <CardWithTable
        isActive={hasData}
        gridArea="table"
        title="Repasses de Tarifas"
        subtitle={subtitle}
        valueColor={theme.colors.emphasis}
        data={monthlyFaresState.partnerPassedOn}
        pdf={{
          title: 'Tarifas de Contas Repassadas para Parceiros',
          body: <PdfMonthlyPartnersPassedOn />,
        }}
      />

      <Card
        isActive={!isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Ultimos 12 Meses"
        subtitle={subtitle}
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={monthlyFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.red}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas   `,
            secondLabel: 'Tarifas repassadas',
          }}
        />
      </Card>

      <Card
        isActive={isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Ultimos 12 Meses"
        subtitle={subtitle}
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={monthlyFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.blue}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas (Fidúcia)   `,
            secondLabel: 'Tarifas repassadas (Parceiros)',
          }}
        />
      </Card>
    </Frame.Base>
  );
}

export default MonthlyDashboard;
